import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { MaterialDto, PagedResult } from '@/types/materials';
import type { MaterialRequestDto } from '@/types/outbound';
import type {
  CreateProductionReceiptRequest,
  ProductionOrderDto,
  ProductionReceiptDto,
  UpdateProductionReceiptRequest,
} from '@/types/production';

interface ReceiptFormValues {
  producedQty: number;
  acceptedQty: number;
  rejectedQty: number;
  batchNo: string;
  mfgDate: string;
  expiryDate: string;
  unitCost: number | '';
}

const EMPTY_FORM: ReceiptFormValues = {
  producedQty: 0, acceptedQty: 0, rejectedQty: 0, batchNo: '', mfgDate: '', expiryDate: '', unitCost: '',
};

// Shared by both tabs' create forms and the edit dialog — same fields either way. Location is no
// longer captured here at all — Production Putaway owns it now, same as GRN never asking for a
// location on the receipt itself. Material isn't part of this either — Tab 1 gets it implicitly
// from the selected order, Tab 2 has its own dedicated Material picker.
function ReceiptLineFields({
  values,
  onChange,
}: {
  values: ReceiptFormValues;
  onChange: <K extends keyof ReceiptFormValues>(key: K, value: ReceiptFormValues[K]) => void;
}) {
  const { t } = useTranslation('production');
  return (
    <GridContainer columns={2} spacing={2}>
      <GridItem xs={1}>
        <TextField type="number" label={t('receipt.producedQty')} value={values.producedQty} onChange={(e) => onChange('producedQty', Number(e.target.value))} fullWidth />
      </GridItem>
      <GridItem xs={1}>
        <TextField type="number" label={t('receipt.acceptedQty')} value={values.acceptedQty} onChange={(e) => onChange('acceptedQty', Number(e.target.value))} fullWidth />
      </GridItem>
      <GridItem xs={1}>
        <TextField type="number" label={t('receipt.rejectedQty')} value={values.rejectedQty} onChange={(e) => onChange('rejectedQty', Number(e.target.value))} fullWidth />
      </GridItem>
      <GridItem xs={1}>
        <TextField label={t('receipt.batchNo')} value={values.batchNo} onChange={(e) => onChange('batchNo', e.target.value)} fullWidth />
      </GridItem>
      <GridItem xs={1}>
        <TextField type="date" label={t('receipt.mfgDate')} value={values.mfgDate} onChange={(e) => onChange('mfgDate', e.target.value)} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
      </GridItem>
      <GridItem xs={1}>
        <TextField type="date" label={t('receipt.expiryDate')} value={values.expiryDate} onChange={(e) => onChange('expiryDate', e.target.value)} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
      </GridItem>
      <GridItem xs={1}>
        <TextField
          type="number" label={t('receipt.unitCost')} value={values.unitCost}
          onChange={(e) => onChange('unitCost', e.target.value === '' ? '' : Number(e.target.value))} fullWidth
        />
      </GridItem>
    </GridContainer>
  );
}

// Accepted + Rejected together must not exceed the order's Planned Qty — a flat per-receipt cap,
// not a running total across every receipt posted against the order. Only applies to
// Production-Order-sourced receipts — a Material Request has no PlannedQty to check.
// Produced Qty is deliberately NOT added into this sum: it's the operator's own reported raw
// output count, which the Accepted/Rejected split already accounts for (order.ProducedQty on the
// backend is only ever incremented by AcceptedQty, never by this line-level field) — summing all
// three used to triple-count a normal, fully-accepted receipt and made it impossible to ever post
// 100% of a plan (e.g. Produced 2 + Accepted 2 + Rejected 0 against a Planned Qty of 2 used to read
// as "4 > 2" and block the button, even though the receipt was entered correctly).
function planQtyExceeded(values: ReceiptFormValues, plannedQty: number) {
  return values.acceptedQty + values.rejectedQty > plannedQty;
}

function toLineInput(values: ReceiptFormValues, materialId: string) {
  return {
    materialId,
    producedQty: values.producedQty, acceptedQty: values.acceptedQty, rejectedQty: values.rejectedQty,
    batchNo: values.batchNo || null, mfgDate: values.mfgDate || null, expiryDate: values.expiryDate || null,
    unitCost: values.unitCost === '' ? null : values.unitCost,
  };
}

export default function ProductionReceiptPage() {
  const { t } = useTranslation('production');
  const queryClient = useQueryClient();

  const [sourceTab, setSourceTab] = useState<'productionPlan' | 'materialRequest'>('productionPlan');

  const { data: config } = useProcessConfiguration();
  // Each tab mirrors the visibility of its own source screen (Production Plan / Material Request)
  // in Process Configuration — independent of productionReceiptEnabled, which only gates this
  // screen's own presence in the nav. Falls back to showing both if a company somehow disables
  // both sources, same fallback convention used for other mode-toggle groups in this codebase.
  const productionPlanTabVisible = (config?.productionEnabled ?? true) || !(config?.materialRequestEnabled ?? true);
  const materialRequestTabVisible = (config?.materialRequestEnabled ?? true) || !(config?.productionEnabled ?? true);

  useEffect(() => {
    if (sourceTab === 'productionPlan' && !productionPlanTabVisible) setSourceTab('materialRequest');
    if (sourceTab === 'materialRequest' && !materialRequestTabVisible) setSourceTab('productionPlan');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionPlanTabVisible, materialRequestTabVisible]);

  const { data: orders = [] } = useQuery({
    queryKey: ['production-orders'],
    queryFn: async () => (await apiClient.get<ProductionOrderDto[]>('/production-orders')).data,
  });
  const { data: materialRequests = [] } = useQuery({
    queryKey: ['material-requests'],
    queryFn: async () => (await apiClient.get<MaterialRequestDto[]>('/material-requests')).data,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];
  const producibleMaterials = materials.filter((m) => m.materialType === 'FG' || m.materialType === 'SFG');
  const { data: receipts = [], isLoading: loadingReceipts } = useQuery({
    queryKey: ['production-receipts'],
    queryFn: async () => (await apiClient.get<ProductionReceiptDto[]>('/production-receipts')).data,
  });

  // --- Tab 1: Production Plan (existing flow, unchanged) ---

  // Only offer orders whose Material Issue is fully done — every component line's IssuedQty has
  // reached its RequiredQty. Receiving FG/SFG before all its components are actually issued would
  // post a receipt with nothing to back it.
  const materialIssueComplete = (o: ProductionOrderDto) =>
    o.lines.length > 0 && o.lines.every((l) => l.issuedQty >= l.requiredQty);
  const openOrders = orders.filter((o) => o.status === 'Released' && !o.isClosed && materialIssueComplete(o));

  const [orderId, setOrderId] = useState('');
  const selectedOrder = openOrders.find((o) => o.id === orderId) ?? null;

  const [form, setForm] = useState<ReceiptFormValues>(EMPTY_FORM);
  const setField = <K extends keyof ReceiptFormValues>(key: K, value: ReceiptFormValues[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const selectOrder = (id: string) => {
    setOrderId(id);
    setForm(EMPTY_FORM);
  };

  const receiveMutation = useMutation({
    mutationFn: () => {
      const payload: CreateProductionReceiptRequest = {
        productionOrderId: orderId, materialRequestId: null, lines: [toLineInput(form, selectedOrder!.materialId)],
      };
      return apiClient.post('/production-receipts', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-receipts'] });
      setOrderId('');
      setForm(EMPTY_FORM);
    },
  });

  // --- Tab 2: Material Request — only requests whose picking has completed (and stock updated)
  // are eligible; the operator separately picks which FG/SFG resulted from the issued materials,
  // since the request's own lines are RM inputs, not an output. ---

  const eligibleMaterialRequests = materialRequests.filter((mr) => mr.status === 'Completed');

  const [mrId, setMrId] = useState('');
  const [mrMaterialId, setMrMaterialId] = useState('');
  const [mrForm, setMrForm] = useState<ReceiptFormValues>(EMPTY_FORM);
  const setMrField = <K extends keyof ReceiptFormValues>(key: K, value: ReceiptFormValues[K]) =>
    setMrForm((prev) => ({ ...prev, [key]: value }));

  const selectMr = (id: string) => {
    setMrId(id);
    setMrMaterialId('');
    setMrForm(EMPTY_FORM);
  };

  const receiveFromMrMutation = useMutation({
    mutationFn: () => {
      const payload: CreateProductionReceiptRequest = {
        productionOrderId: null, materialRequestId: mrId, lines: [toLineInput(mrForm, mrMaterialId)],
      };
      return apiClient.post('/production-receipts', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['material-requests'] });
      queryClient.invalidateQueries({ queryKey: ['production-receipts'] });
      setMrId('');
      setMrMaterialId('');
      setMrForm(EMPTY_FORM);
    },
  });

  // --- Edit (either source) — only while Status is still PendingApproval or Approved (see
  // ProductionReceiptService.UpdateAsync's guard). Material is fixed once created; shown read-only. ---

  const [editingReceipt, setEditingReceipt] = useState<ProductionReceiptDto | null>(null);
  const [editForm, setEditForm] = useState<ReceiptFormValues>(EMPTY_FORM);
  const [editMaterialId, setEditMaterialId] = useState('');
  const setEditField = <K extends keyof ReceiptFormValues>(key: K, value: ReceiptFormValues[K]) =>
    setEditForm((prev) => ({ ...prev, [key]: value }));

  const editingOrder = orders.find((o) => o.id === editingReceipt?.productionOrderId) ?? null;

  const openEdit = (r: ProductionReceiptDto) => {
    const line = r.lines[0];
    setEditingReceipt(r);
    setEditMaterialId(line.materialId);
    setEditForm({
      producedQty: line.producedQty, acceptedQty: line.acceptedQty, rejectedQty: line.rejectedQty,
      batchNo: line.batchNo ?? '', mfgDate: line.mfgDate?.slice(0, 10) ?? '', expiryDate: line.expiryDate?.slice(0, 10) ?? '',
      unitCost: line.unitCost ?? '',
    });
  };

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editingReceipt) throw new Error('No receipt selected.');
      const payload: UpdateProductionReceiptRequest = { lines: [toLineInput(editForm, editMaterialId)] };
      return apiClient.put(`/production-receipts/${editingReceipt.id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['material-requests'] });
      queryClient.invalidateQueries({ queryKey: ['production-receipts'] });
      setEditingReceipt(null);
    },
  });

  const historyColumns: DataTableColumn<ProductionReceiptDto>[] = [
    { field: 'receiptNumber', headerName: t('table.number') },
    { field: 'productionOrderNumber', headerName: t('table.source'), render: (r) => r.productionOrderNumber ?? r.materialRequestNumber ?? '—', filterable: true },
    { field: 'warehouseName', headerName: t('table.warehouse'), filterable: true },
    { field: 'receiptDate', headerName: t('table.date'), render: (r) => new Date(r.receiptDate).toLocaleString() },
    {
      field: 'lines', headerName: t('table.acceptedQty'),
      render: (r) => r.lines.reduce((sum, l) => sum + l.acceptedQty, 0),
    },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: t('table.actions'), render: (r) => (
        <IfPermitted permission={PERMISSIONS.PRODUCTION_EXECUTE}>
          {(r.status === 'PendingApproval' || r.status === 'Approved') && (
            <Button size="small" onClick={() => openEdit(r)}>
              {t('edit')}
            </Button>
          )}
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('receipt.title')} subtitle={t('receipt.subtitle')} />

      {/* Hidden entirely when only one source is enabled — nothing to switch between. */}
      {productionPlanTabVisible && materialRequestTabVisible && (
        <Tabs value={sourceTab} onChange={(_, v: 'productionPlan' | 'materialRequest') => setSourceTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="productionPlan" label={t('receipt.tabs.productionPlan')} />
          <Tab value="materialRequest" label={t('receipt.tabs.materialRequest')} />
        </Tabs>
      )}

      {sourceTab === 'productionPlan' && productionPlanTabVisible && (
        <Stack spacing={2} sx={{ maxWidth: 640, mb: 4 }}>
          <SearchableSelect
            label={t('receipt.selectOrder')} value={orderId} onChange={selectOrder} fullWidth
            options={openOrders.map((o) => ({
              value: o.id,
              label: `${o.orderNumber} — ${o.materialCode} (${t('table.plannedQty')}: ${o.plannedQty}, ${t('table.producedQty')}: ${o.producedQty})`,
            }))}
          />

          {selectedOrder && (
            <>
              <ReceiptLineFields values={form} onChange={setField} />

              {planQtyExceeded(form, selectedOrder.plannedQty) && (
                <Alert severity="error">{t('receipt.planQtyExceeded', { plannedQty: selectedOrder.plannedQty })}</Alert>
              )}

              <Button
                variant="contained" size="large"
                disabled={form.acceptedQty + form.rejectedQty <= 0 || planQtyExceeded(form, selectedOrder.plannedQty) || receiveMutation.isPending}
                onClick={() => receiveMutation.mutate()}
              >
                {receiveMutation.isPending ? t('receipt.posting') : t('receipt.postButton')}
              </Button>
            </>
          )}
        </Stack>
      )}

      {sourceTab === 'materialRequest' && materialRequestTabVisible && (
        <Stack spacing={2} sx={{ maxWidth: 640, mb: 4 }}>
          <SearchableSelect
            label={t('receipt.selectMaterialRequest')} value={mrId} onChange={selectMr} fullWidth
            helperText={eligibleMaterialRequests.length === 0 ? t('receipt.noEligibleMaterialRequests') : undefined}
            options={eligibleMaterialRequests.map((mr) => ({ value: mr.id, label: `${mr.mrNumber} — ${mr.warehouseName}` }))}
          />

          {mrId && (
            <>
              <SearchableSelect
                label={t('receipt.selectMaterial')} value={mrMaterialId} onChange={setMrMaterialId} fullWidth
                options={producibleMaterials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name} (${m.materialType})` }))}
              />

              {mrMaterialId && (
                <>
                  <ReceiptLineFields values={mrForm} onChange={setMrField} />

                  <Button
                    variant="contained" size="large"
                    disabled={mrForm.acceptedQty + mrForm.rejectedQty <= 0 || receiveFromMrMutation.isPending}
                    onClick={() => receiveFromMrMutation.mutate()}
                  >
                    {receiveFromMrMutation.isPending ? t('receipt.posting') : t('receipt.postButton')}
                  </Button>
                </>
              )}
            </>
          )}
        </Stack>
      )}

      <DataTable columns={historyColumns} rows={receipts} loading={loadingReceipts} searchable getRowId={(r) => r.id} exportFileName="production-receipts" />

      <Dialog open={Boolean(editingReceipt)} onClose={() => setEditingReceipt(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingReceipt?.receiptNumber}
          <Typography variant="body2" color="text.secondary">
            {editingReceipt?.productionOrderNumber ?? editingReceipt?.materialRequestNumber} · {editingReceipt?.lines[0]?.materialCode} — {editingReceipt?.lines[0]?.materialName}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <ReceiptLineFields values={editForm} onChange={setEditField} />

            {editingOrder && planQtyExceeded(editForm, editingOrder.plannedQty) && (
              <Alert severity="error">{t('receipt.planQtyExceeded', { plannedQty: editingOrder.plannedQty })}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditingReceipt(null)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={
              editForm.acceptedQty + editForm.rejectedQty <= 0 ||
              Boolean(editingOrder && planQtyExceeded(editForm, editingOrder.plannedQty)) ||
              updateMutation.isPending
            }
            onClick={() => updateMutation.mutate()}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
